export interface JdFeeRatioDailyRow {
  date: string
  gmv: number
  regularJzt: number
  regularJztStatus: string
  brandAllocated: number
  assessmentSpend: number
  dailyRate: number | null
}

export interface JdFeeRatioPayload {
  schemaVersion: string
  generatedAt: string
  storeId: string
  currency: 'CNY'
  month: string
  dataThrough: string
  monthDays: number
  remainingDays: number
  target: {
    platformMinimumRate: number
    benchmarkMonth: string
    benchmarkGmv: number
    benchmarkRegularJzt: number
    benchmarkBrandAllocated: number
    benchmarkAssessmentSpend: number
    benchmarkRate: number
    effectiveRate: number
  }
  brandAllocation: {
    contractName: string
    quarter: string
    validFrom: string
    validTo: string
    amount: number
    dayCount: number
    dailyAmount: number
  }
  actual: {
    daysElapsed: number
    gmv: number
    regularJzt: number
    brandAllocated: number
    assessmentSpend: number
    rate: number | null
    rows: JdFeeRatioDailyRow[]
  }
  scope: {
    included: string[]
    excluded: string[]
  }
  sources: {
    sales: { sourceSystem: string; pageId: string; attribution: string; collectedAt: string }
    advertising: { sourceSystem: string; updatedAt: string }
    benchmark: { year: number; updatedAt: string; assessmentScope: string }
  }
}

export interface JdFeeRatioActualRow extends JdFeeRatioDailyRow {
  cumulativeGmv: number
  cumulativeSpend: number
  cumulativeRate: number | null
}

export interface JdFeeRatioPlanRow {
  date: string
  forecastGmv: number
  brandAllocated: number
  regularJztRequired: number
  projectedCumulativeRate: number | null
}

export interface JdFeeRatioPlan {
  dailyForecastGmv: number
  forecastRemainingGmv: number
  forecastMonthGmv: number
  currentGap: number
  remainingAssessmentSpend: number
  remainingBrandAllocated: number
  remainingRegularJzt: number
  dailyRegularJzt: number
  requiredRemainingRate: number | null
  rows: JdFeeRatioPlanRow[]
}

function nextDate(dateText: string, offset: number) {
  const [year, month, day] = dateText.split('-').map(Number)
  const result = new Date(Date.UTC(year, month - 1, day + offset))
  return result.toISOString().slice(0, 10)
}

export function cumulativeFeeRatioRows(payload: JdFeeRatioPayload): JdFeeRatioActualRow[] {
  let cumulativeGmv = 0
  let cumulativeSpend = 0
  return payload.actual.rows.map((row) => {
    cumulativeGmv += row.gmv
    cumulativeSpend += row.assessmentSpend
    return {
      ...row,
      cumulativeGmv,
      cumulativeSpend,
      cumulativeRate: cumulativeGmv > 0 ? cumulativeSpend / cumulativeGmv : null,
    }
  })
}

export function monthToDateAverage(payload: JdFeeRatioPayload) {
  return payload.actual.daysElapsed > 0 ? payload.actual.gmv / payload.actual.daysElapsed : 0
}

export function recentSevenDayAverage(payload: JdFeeRatioPayload) {
  const rows = payload.actual.rows.slice(-7)
  return rows.length ? rows.reduce((total, row) => total + row.gmv, 0) / rows.length : 0
}

export function calculateJdFeeRatioPlan(payload: JdFeeRatioPayload, dailyForecastGmv: number): JdFeeRatioPlan {
  const safeDailyForecast = Number.isFinite(dailyForecastGmv) ? Math.max(0, dailyForecastGmv) : 0
  const forecastRemainingGmv = safeDailyForecast * payload.remainingDays
  const forecastMonthGmv = payload.actual.gmv + forecastRemainingGmv
  const requiredMonthSpend = forecastMonthGmv * payload.target.effectiveRate
  const remainingAssessmentSpend = Math.max(0, requiredMonthSpend - payload.actual.assessmentSpend)
  const remainingBrandAllocated = payload.brandAllocation.dailyAmount * payload.remainingDays
  const remainingRegularJzt = Math.max(0, remainingAssessmentSpend - remainingBrandAllocated)
  const dailyRegularJzt = payload.remainingDays > 0 ? remainingRegularJzt / payload.remainingDays : 0
  const currentGap = Math.max(0, payload.actual.gmv * payload.target.effectiveRate - payload.actual.assessmentSpend)
  const requiredRemainingRate = forecastRemainingGmv > 0 ? remainingAssessmentSpend / forecastRemainingGmv : null
  let projectedGmv = payload.actual.gmv
  let projectedSpend = payload.actual.assessmentSpend
  const rows = Array.from({ length: payload.remainingDays }, (_, index) => {
    projectedGmv += safeDailyForecast
    projectedSpend += dailyRegularJzt + payload.brandAllocation.dailyAmount
    return {
      date: nextDate(payload.dataThrough, index + 1),
      forecastGmv: safeDailyForecast,
      brandAllocated: payload.brandAllocation.dailyAmount,
      regularJztRequired: dailyRegularJzt,
      projectedCumulativeRate: projectedGmv > 0 ? projectedSpend / projectedGmv : null,
    }
  })
  return {
    dailyForecastGmv: safeDailyForecast,
    forecastRemainingGmv,
    forecastMonthGmv,
    currentGap,
    remainingAssessmentSpend,
    remainingBrandAllocated,
    remainingRegularJzt,
    dailyRegularJzt,
    requiredRemainingRate,
    rows,
  }
}
